import {
  Paper,
  Typography,
  ListItemIcon,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
} from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useMemo, useState } from 'react';

import { ArrowSmallDown } from '@/assets/icons';
import { Theme } from '@/modules/common/types/general';
import { StyledTooltip } from '@/helpers/styles';
import { UserPreferenceName } from '@/modules/userPreferences';
import { useUserPreference } from '@/modules/userPreferences/hooks';
import { MultiSelectInputOption } from './MultiSelectInputOption';
import { useAppStore } from '@/modules/app/store/useAppStore';

type Option = {
  value: string;
  label: string;
};

type Props = {
  selectedOptions: string[];
  options: Option[];
  onItemClick: (value: string) => void;
  displayValue: string;
  disabled?: boolean;
  optionListMinWidth?: string;
  onClose?: () => void;
};

function MultiSelectInputComponent({
  selectedOptions,
  options,
  onItemClick,
  displayValue,
  disabled = false,
  optionListMinWidth = undefined,
  onClose = undefined,
}: Props) {
  const theme = useUserPreference(UserPreferenceName.GENERAL_THEME);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const mode = useAppStore((state) => state.mode);

  const handleOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleClick = (value: string): void => {
    onItemClick(value);
  };

  const optionListSx = useMemo(
    () => ({
      ...(optionListMinWidth !== undefined ? { minWidth: optionListMinWidth } : {}),
    }),
    [optionListMinWidth],
  );

  const open = Boolean(anchorEl);

  const isDisabled = useMemo(() => disabled || mode !== 'EDITABLE', [disabled, mode]);

  return (
    <>
      <Stack sx={{ width: '100%' }}>
        <StyledTooltip title={open ? undefined : displayValue}>
          <Paper
            onClick={isDisabled ? undefined : handleOpen}
            sx={{
              width: '100%',
              minHeight: '32px',
              color: 'rgba(0, 0, 0, 0.87)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              paddingLeft: '10px',
              paddingRight: '6px',
              cursor: 'pointer',
              background: 'rgb(248, 248, 252)',
              borderRadius: 0,
              ...(isDisabled && {
                cursor: 'not-allowed',
                color: 'rgba(0, 0, 0, 0.38)',
                background: 'rgba(0, 0, 0, 0.04)',
              }),
            }}
            elevation={0}
          >
            <Typography
              sx={{
                flex: '1 1 auto',
                padding: 0,
                maxHeight: '100%',
                fontSize: '12px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...(isDisabled && { opacity: 0.5 }),
              }}
            >
              {displayValue}
            </Typography>
            <ListItemIcon
              sx={{
                flex: '0 0 auto',
                minWidth: (theme) => theme.spacing(3),
                fontSize: 18,
                transform: open ? 'rotate(180deg)' : undefined,
                ...(isDisabled && { opacity: 0.5 }),
              }}
            >
              <ArrowSmallDown />
            </ListItemIcon>
          </Paper>
        </StyledTooltip>
      </Stack>
      <Popper
        anchorEl={anchorEl}
        placement='bottom-end'
        sx={{
          zIndex: 2000,
          width: 'fit-content',
          maxWidth: '360px',
          height: 'auto',
        }}
        className={theme === Theme.DARK ? 'dark' : ''}
        open={open}
        transition
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'center top',
            }}
          >
            <Paper
              sx={{
                boxShadow:
                  '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',

                maxHeight: '250px',
                overflowY: 'scroll',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },

                '&:hover': { cursor: 'default' },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList sx={optionListSx}>
                  {options.map(({ label, value }) => (
                    <MultiSelectInputOption
                      key={value}
                      value={value}
                      label={label}
                      onClick={handleClick}
                      isSelected={selectedOptions.includes(value)}
                    />
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export const MultiSelectInput = memo(MultiSelectInputComponent);
